import React, { useState } from "react";
import Functions from "../../utility/services/Functions";
import { IconButton, Tooltip, Box, Typography } from "@mui/material";
import { Remove } from "@mui/icons-material";
import { useCallDuration } from "../../utility/services/AuthContext";

const VoiceCallScreen = ({ message, onEndCall, role, isAccept, onMinimize, userName }) => {
  const { formatCallDuration } = Functions();
  // Use custom hook for live duration updates (only this component re-renders)
  const callDuration = useCallDuration();
  const [callEnded, setCallEnded] = useState(false);

  const isCaller = role == "caller";

  // DEBUG: Log userName prop to understand when it's available
  React.useEffect(() => {
    console.log('🎤 VoiceCallScreen - userName prop:', {
      userName: userName,
      hasUserName: !!userName,
      role: role,
      isAccept: isAccept,
      message: message
    });
  }, [userName, role, isAccept, message]);

  // NOTE: Duration is tracked globally in AuthContext ref
  // useCallDuration hook provides live updates without causing context re-renders

  const startTiming = () => {
    // NOTE: Using isAccept instead of interVal (local timer removed)
    if (!isAccept) {
      setCallEnded(false);
    } else {
      onEndCall();
      setCallEnded(true);
    }
  };

  const endCall = () => {
    onEndCall();
    setCallEnded(true);
  };

  return (
    <div className="voice-call-container">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          backdropFilter: "blur(60px) saturate(200%)",
          WebkitBackdropFilter: "blur(60px) saturate(200%)",
          background: "rgba(17, 25, 40, 0.15)",
          borderRadius: "16px",
          border: "1px solid rgba(255, 255, 255, 0.15)",
          boxShadow: "0 12px 48px rgba(0, 0, 0, 0.6)",
          minWidth: { xs: "auto", sm: "500px" },
          maxWidth: { xs: "100%", sm: "600px" },
          width: "100%",
        }}
      >
        {/* Compact Header - Centered Text with Right Button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "12px 24px",
            background: "rgba(29, 126, 124, 0.5)",
            borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
            borderTopLeftRadius: "16px",
            borderTopRightRadius: "16px",
            position: "relative",
            backdropFilter: "blur(50px) saturate(200%)",
            WebkitBackdropFilter: "blur(50px) saturate(200%)",
          }}
        >
        <Typography
          variant="h6"
          sx={{
            color: "#f8fafc",
            margin: 0,
            fontSize: "15px",
            fontWeight: 500,
            letterSpacing: "0.3px",
          }}
        >
          Voice Call
        </Typography>

        {/* Minimize Button - Absolute Right */}
        {onMinimize && (
          <Tooltip title="Minimize" placement="left">
            <IconButton
              onClick={onMinimize}
              sx={{
                position: "absolute",
                right: { xs: "8px", sm: "16px" },
                color: "#f8fafc",
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "#ffffff",
                },
                width: { xs: 32, sm: 34 },
                height: { xs: 32, sm: 34 },
                transition: "all 0.2s ease",
                zIndex: 10,
              }}
            >
              <Remove sx={{ fontSize: { xs: 18, sm: 20 } }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Call Content - Dark Animated Theme with Blur */}
      <Box
        sx={{
          background: "linear-gradient(135deg, rgba(15, 23, 42, 0.85) 0%, rgba(30, 41, 59, 0.85) 50%, rgba(15, 23, 42, 0.85) 100%)",
          backgroundSize: "200% 200%",
          animation: "gradientShift 8s ease infinite",
          backdropFilter: "blur(15px)",
          borderBottomLeftRadius: "16px",
          borderBottomRightRadius: "16px",
          padding: "48px 32px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "480px",
          justifyContent: "center",
          position: "relative",
          "@keyframes gradientShift": {
            "0%": {
              backgroundPosition: "0% 50%",
            },
            "50%": {
              backgroundPosition: "100% 50%",
            },
            "100%": {
              backgroundPosition: "0% 50%",
            },
          },
        }}
      >
        {/* Animated Phone Icon Circle */}
        <Box
          sx={{
            width: "140px",
            height: "140px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, #4ade80 0%, #22c55e 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "32px",
            boxShadow: "0 0 40px rgba(74, 222, 128, 0.5), 0 0 80px rgba(74, 222, 128, 0.2)",
            animation: callEnded ? "none" : "pulse 2s ease-in-out infinite",
            "@keyframes pulse": {
              "0%, 100%": {
                transform: "scale(1)",
                boxShadow: "0 0 40px rgba(74, 222, 128, 0.5), 0 0 80px rgba(74, 222, 128, 0.2)",
              },
              "50%": {
                transform: "scale(1.05)",
                boxShadow: "0 0 50px rgba(74, 222, 128, 0.6), 0 0 100px rgba(74, 222, 128, 0.3)",
              },
            },
          }}
        >
          <span style={{ fontSize: "60px" }}>📞</span>
        </Box>

        {/* User Name Display */}
        {userName && (
          <Box
            sx={{
              textAlign: "center",
              marginBottom: "16px",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: "#f1f5f9",
                fontSize: "24px",
                fontWeight: 600,
                letterSpacing: "0.5px",
              }}
            >
              {userName}
            </Typography>
          </Box>
        )}

        {/* Call Status Message */}
        <Box
          sx={{
            textAlign: "center",
            marginBottom: "28px",
            maxWidth: "420px",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: "#e2e8f0",
              fontSize: "17px",
              lineHeight: "1.7",
              fontWeight: 400,
            }}
          >
            {!isAccept ? (callEnded ? "Call ended" : message) : message}
          </Typography>
        </Box>

        {/* Timer Display (when call is accepted) */}
        {isAccept && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              marginBottom: "36px",
              padding: "14px 28px",
              background: "linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)",
              borderRadius: "50px",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(16, 185, 129, 0.2)",
              boxShadow: "0 4px 12px rgba(16, 185, 129, 0.1)",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: "#f1f5f9",
                fontSize: "26px",
                fontWeight: 600,
                letterSpacing: "1px",
              }}
            >
              ⏱️ {formatCallDuration(callDuration)}
            </Typography>
          </Box>
        )}

        {/* End Call Button - Dark Theme Style */}
        <Box
          sx={{
            marginTop: "auto",
          }}
        >
          <button
            onClick={isCaller ? endCall : startTiming}
            style={{
              background: "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)",
              color: "#fef2f2",
              border: "1px solid rgba(220, 38, 38, 0.5)",
              borderRadius: "50px",
              padding: "16px 56px",
              fontSize: "16px",
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "0 8px 24px rgba(220, 38, 38, 0.35), 0 0 0 1px rgba(220, 38, 38, 0.1)",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 12px 32px rgba(220, 38, 38, 0.45), 0 0 0 1px rgba(220, 38, 38, 0.2)";
              e.target.style.background = "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 8px 24px rgba(220, 38, 38, 0.35), 0 0 0 1px rgba(220, 38, 38, 0.1)";
              e.target.style.background = "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)";
            }}
          >
            End Call
          </button>
        </Box>
      </Box>
      </Box>
    </div>
  );
};

export default VoiceCallScreen;
